import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { TremorLogo } from '@/shared/components/ui/TremorLogo'
import { User, Lock, Eye, EyeOff, ArrowRight, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import api from '@/shared/lib/api'

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  }

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!fullName.trim()) { setError('Veuillez saisir votre nom.'); return }
    if (password !== confirmPassword) { setError('Les mots de passe ne correspondent pas.'); return }
    if (!passwordChecks.length || !passwordChecks.uppercase || !passwordChecks.number) {
      setError('Le mot de passe ne respecte pas les critères.'); return
    }

    setLoading(true)
    try {
      await api.post(`/users/invite/${token}/accept`, {
        full_name: fullName.trim(),
        password,
      })
      setSuccess(true)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(detail || 'Erreur lors de la création du compte.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-center mb-8">
            <TremorLogo size={40} className="text-gold" />
          </div>
          <div className="w-16 h-16 mx-auto rounded-full bg-teal-light flex items-center justify-center mb-4">
            <CheckCircle2 size={32} className="text-teal-dark" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-ink mb-2">Compte créé !</h1>
          <p className="text-[14px] text-ink-muted mb-6">
            Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-onyx text-paper text-[14px] font-medium hover:bg-onyx-light transition-colors"
          >
            Se connecter <ArrowRight size={16} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* Left — Onyx branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-onyx flex-col justify-between p-12 overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-gold/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-brick/15 blur-[100px]" />
        <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full bg-teal/10 blur-[80px]" />

        <div className="relative z-10 flex items-center gap-3">
          <TremorLogo size={32} className="text-gold" />
          <p className="font-serif text-2xl font-bold text-paper tracking-tight">
            tremor<em className="not-italic font-light text-gold">app</em>
          </p>
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="font-serif text-4xl font-bold text-paper tracking-tight leading-tight">
            Vous êtes<br />
            invité à<br />
            <em className="italic text-gold">collaborer.</em>
          </h1>
          <p className="font-sans text-base font-light text-paper/60 max-w-md leading-relaxed">
            Un membre de votre équipe vous a invité à rejoindre son espace TremorApp. Créez votre compte pour commencer.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-2">
          <TremorLogo size={14} className="text-gold/40" />
          <p className="text-[9px] tracking-[0.2em] uppercase text-gold/40">Intelligence Marché · SaaS B2B</p>
        </div>
      </div>

      {/* Right — Accept form */}
      <div className="flex-1 flex items-center justify-center bg-paper border-l border-ink-border/30 px-6 py-12">
        <div className="w-full max-w-[380px] space-y-8">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 lg:hidden">
            <TremorLogo size={24} className="text-gold" />
            <p className="font-serif text-xl font-bold text-ink tracking-tight">
              tremor<em className="not-italic font-light text-gold">app</em>
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="font-serif text-2xl font-bold text-ink">Accepter l'invitation</h2>
            <p className="font-sans text-sm font-light text-ink-muted">Créez votre compte pour rejoindre l'équipe</p>
          </div>

          <form onSubmit={handleAccept} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-brick-light border border-brick/20 px-4 py-3 text-sm text-brick-dark flex items-center gap-2">
                <XCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="block text-label-caps text-ink-muted uppercase tracking-wider">Nom complet</label>
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jean Dupont" autoComplete="name"
                  className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-3 text-sm text-ink placeholder:text-ink-faint shadow-card focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-label-caps text-ink-muted uppercase tracking-wider">Mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 caractères" autoComplete="new-password"
                  className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-11 py-3 text-sm text-ink placeholder:text-ink-faint shadow-card focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink transition-colors">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="flex gap-3 mt-2">
                {[
                  { ok: passwordChecks.length, label: '8+ car.' },
                  { ok: passwordChecks.uppercase, label: '1 maj.' },
                  { ok: passwordChecks.number, label: '1 chiffre' },
                ].map((c) => (
                  <span key={c.label} className={`flex items-center gap-1 text-[11px] ${c.ok ? 'text-semantic-success' : 'text-ink-faint'}`}>
                    <CheckCircle2 size={12} /> {c.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-label-caps text-ink-muted uppercase tracking-wider">Confirmer</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retapez votre mot de passe" autoComplete="new-password"
                  className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-3 text-sm text-ink placeholder:text-ink-faint shadow-card focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brick px-4 py-3 text-sm font-medium text-white transition-all hover:bg-brick-hover active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <Loader2 size={16} className="animate-spin" /> : (<>Créer mon compte <ArrowRight size={16} /></>)}
            </button>
          </form>

          <p className="text-center text-sm text-ink-muted">
            Déjà un compte ?{' '}
            <Link to="/login" className="font-medium text-brick hover:text-brick-dark transition-colors">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
